/** 修正メモの入力ダイアログ(単語・ステージ・まなび問題 共通)。 */
import { useState } from 'react';
import {
  REQUEST_KINDS_BY_TARGET,
  REQUEST_KIND_LABELS,
  REQUEST_TARGET_TYPE_LABELS,
  RequestError,
  type ContentRequestService,
  type NewContentRequest,
  type RequestKind,
  type RequestTargetType,
} from '../services/requests';

export interface RequestTarget {
  readonly type: RequestTargetType;
  readonly id: string;
  readonly label: string;
  readonly language: string;
  /** まなびの帯(kinder / e1 …)。単語・ステージでは付けない。 */
  readonly band?: string;
  /** 種別セレクトの初期値。 */
  readonly defaultKind: RequestKind;
}

interface Props {
  readonly target: RequestTarget;
  readonly service: ContentRequestService;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

export function RequestDialog({ target, service, onClose, onCreated }: Props) {
  const [kind, setKind] = useState<RequestKind>(target.defaultKind);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setError(null);
    setBusy(true);
    const input: NewContentRequest = {
      targetType: target.type,
      targetId: target.id,
      targetLabel: target.label,
      language: target.language,
      ...(target.band === undefined ? {} : { band: target.band }),
      kind,
      body,
    };
    try {
      await service.create(input);
      onCreated();
    } catch (cause) {
      setError(
        cause instanceof RequestError
          ? cause.message
          : `保存に失敗しました(権限またはネットワーク): ${String(cause)}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label="修正メモ">
        <h2 className="dialog__title">修正メモ</h2>
        <p className="dialog__target">
          対象: {REQUEST_TARGET_TYPE_LABELS[target.type]}{' '}
          {target.band !== undefined && <span className="tag">{target.band}</span>}{' '}
          <span className="mono">{target.id}</span> — {target.label}
        </p>

        {error !== null && (
          <div className="notice notice--error" role="alert">
            {error}
          </div>
        )}

        <label className="field">
          <span className="field__label">種別</span>
          <select
            className="select"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as RequestKind);
            }}
          >
            {REQUEST_KINDS_BY_TARGET[target.type].map((value) => (
              <option key={value} value={value}>
                {REQUEST_KIND_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">内容(修正案・理由)</span>
          <textarea
            className="textarea"
            value={body}
            placeholder={
              target.type === 'quiz'
                ? '例: 選択肢の「4」と「6」が近すぎる。「1」に変えたい'
                : '例: 訳を「おはよう!」→「おはようございます」に変えたい'
            }
            onChange={(event) => {
              setBody(event.target.value);
            }}
          />
        </label>

        <div className="notice">
          このメモは <span className="mono">contentRequests</span> に保存されます。実際の content
          への反映は、当面ひとまず開発側(Claude)が fixes.csv 等に適用して再ビルドする運用です。
          GitHub への直接コミットは次フェーズ。
        </div>

        <div className="dialog__actions">
          <button className="button" type="button" onClick={onClose} disabled={busy}>
            キャンセル
          </button>
          <button
            className="button button--primary"
            type="button"
            disabled={busy}
            onClick={() => {
              void submit();
            }}
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
